import type { OpenT, OpenVotesT } from '@mentimeter/question-modules-types';
import { useOnce } from '@mentimeter/react-hooks';
import { useContext, useEffect } from 'react';
import type { OpenEndedStaticContent } from '@core-api/presentation/types/response';
import { ScreenshotDataContext } from '@mentimeter/screenshots/ScreenshotDataContext';
import { trackUser } from '@api/tracking/client';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { getVisualizationState } from '../../utils/getVisualizationState';
import { useTextResponses } from './hooks/use-text-responses';
import { useOpenPreviewResultsLegacy } from './hooks/use-open-preview-results-legacy';
import { getVisualizationStateLegacy } from './presentation/utils/getVisualizationStateLegacy';
import { useOpenSlideState } from './presentation/utils/useOpenSlideState';
import { PresentationModule } from './PresentationModule';
import { OpenEndedPresentationContext } from './OpenEndedPresentationContext';
import { getNumberOfColumns } from './utils/getNumberOfColumns';

export function OpenPresentationDataHandler() {
  const {
    useConfig,
    useResults,
    usePace,
    isPreviewResultsEnabled,
    useSeries,
    useCurrentSession,
  } = usePresentationContext();
  const { mode, enableAnimations, readonly } = useConfig();
  const rawResults = useResults<OpenT>();

  const { step, currentStep } = usePace();

  const {
    slideAdminKey,
    images,
    title,
    staticContent,
    interactiveContents,
    responseVisibility,
  } = usePresentationSlideContext();

  const interactiveContent = interactiveContents[0]!;

  const [{ shouldShowGroups }, setSlideState] = useOpenSlideState();
  const { isCurrentSessionActive, currentSessionId } = useCurrentSession();

  const legacyResponses: OpenVotesT[] =
    rawResults.type === 'open' ? rawResults.votes : [];
  const numberOfParticipants =
    rawResults.type === 'open' ? rawResults.voter_count : 0;
  const isPlaceholderResults = rawResults.isPlaceholderResults;

  const { participation_identity_mode, participation_identity_visibility } =
    useSeries();
  const hasIdentifiedResultsSettingOnAndVisible =
    participation_identity_mode === 'identified' &&
    participation_identity_visibility === 'everyone';

  const shouldFetchIdentifiedResponses =
    hasIdentifiedResultsSettingOnAndVisible && isCurrentSessionActive;

  const shouldShowIdentifiedResponses =
    shouldFetchIdentifiedResponses && !isPreviewResultsEnabled;

  const { responses = [], isLoading: isLoadingTextResponses } =
    useTextResponses({
      adminKey: slideAdminKey,
      interactiveContentId: interactiveContent.interactiveContentId,
      shouldFetchResults: shouldFetchIdentifiedResponses,
      sessionId: isCurrentSessionActive ? undefined : currentSessionId,
      shouldShowIdentifiedResponses,
      legacyResponses,
    });

  const visualizationState = currentStep
    ? getVisualizationState({
        votingOnAnswers:
          interactiveContent.voteSettings?.voteOnResponsePolicy ===
          'allow-votes',
        mode,
        numberOfAnswers: legacyResponses.length,
        currentStep,
      })
    : getVisualizationStateLegacy({
        votingOnAnswers:
          interactiveContent.voteSettings?.voteOnResponsePolicy ===
          'allow-votes',
        mode,
        numberOfAnswers: legacyResponses.length,
        step,
      });

  const shouldHideResults =
    responseVisibility === 'hidden' || visualizationState === 'hide-answers';

  function trackViewedSlideState(
    state: 'start-voting' | 'viewed-upvoting-results',
  ) {
    trackUser({
      event: 'Viewed slide state',
      properties: {
        questionType: 'open',
        state,
        context: shouldShowGroups
          ? 'grouped-responses'
          : 'individual-responses',
      },
    });
  }

  useOnce(visualizationState === 'start-voting', () =>
    trackViewedSlideState('start-voting'),
  );
  useOnce(visualizationState === 'view-results', () =>
    trackViewedSlideState('viewed-upvoting-results'),
  );

  useEffect(() => {
    if (shouldShowGroups) {
      // reset the state when pacing through visualization states
      setSlideState({
        activeClusterId: null,
        activeResponseIndex: null,
      });
    }
  }, [visualizationState, setSlideState, shouldShowGroups]);

  const { setSlideIsReady } = useContext(ScreenshotDataContext);

  useEffect(() => {
    if (mode === 'screenshot' && setSlideIsReady) {
      setSlideIsReady(
        !isLoadingTextResponses &&
          responses !== undefined &&
          !isPlaceholderResults,
      );
    }
  }, [
    mode,
    isLoadingTextResponses,
    responses,
    setSlideIsReady,
    isPlaceholderResults,
  ]);

  const legacyPreviewResults = useOpenPreviewResultsLegacy({
    isEnabled: isPreviewResultsEnabled,
  });

  if (shouldFetchIdentifiedResponses && isLoadingTextResponses) {
    return null;
  }

  const visibleResponses = isPreviewResultsEnabled
    ? legacyPreviewResults.votes.map((vote) => ({
        id: vote.id,
        responseText: vote.answer,
        voteCount: vote.upvotes,
        participantDisplayName: null,
      }))
    : responses;

  const openEndedStaticContent = staticContent as OpenEndedStaticContent;

  const numberOfColumns = getNumberOfColumns({
    hasImages: images.length > 0,
    snapDirection: openEndedStaticContent.layout?.snapDirection ?? 'x',
    imageLayoutPosition: openEndedStaticContent.image?.imageLayoutPosition ?? {
      start: 0,
      end: 0,
    },
    gridColumns: openEndedStaticContent.layout?.gridColumns ?? 0,
  });

  return (
    <OpenEndedPresentationContext.Provider
      value={{
        title,
        styledTitle: openEndedStaticContent.styledTitle as Record<
          string,
          unknown
        >,
        label: openEndedStaticContent.label,
        description: interactiveContent.description,
        textSizeOffset: openEndedStaticContent.textSizeOffset,
        shouldAnimate: enableAnimations,
        isReadonly: readonly,
        mode,
        visualization:
          openEndedStaticContent.interactiveContentStyling?.visualization ??
          'speech_bubbles',
      }}
    >
      <PresentationModule
        visualizationState={visualizationState}
        responses={visibleResponses}
        numberOfVoters={numberOfParticipants}
        isUpvotingEnabled={
          interactiveContent.voteSettings?.voteOnResponsePolicy ===
          'allow-votes'
        }
        shouldHideResults={shouldHideResults}
        mode={mode}
        numberOfColumns={numberOfColumns}
        shouldShowGroups={!!shouldShowGroups}
      />
    </OpenEndedPresentationContext.Provider>
  );
}
